import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 24 }, (_, i) => i + 1); 

const GlobalHeatmap = ({ title, data, color = "#1A73E8", isDashboard = false }) => {
  const CELL_SIZE = "30px";
  const GAP_SIZE = "4px";
  const LABEL_WIDTH = "40px";

  const maxValue = useMemo(() => {
    let max = 0;
    if (!data) return 0;
    Object.values(data).forEach((dayValuesArray) => {
      if (Array.isArray(dayValuesArray)) {
        dayValuesArray.forEach((val) => {
          if (val > max) max = val;
        });
      }
    });
    return max;
  }, [data]);

  const formatHour = (h) => {
    if (h === 12) return "12 PM";
    if (h === 24 || h === 0) return "12 AM";
    return h > 12 ? `${h - 12} PM` : `${h} AM`;
  };

  if (!data) return null;

  return (
    <MDBox
      sx={{
        p: 3,
        width: "100%",
        overflowX: "auto",
      }}
    >
      <MDTypography variant="h5" fontWeight="bold" mb={3} mt={2}>
        {title}
      </MDTypography>

      <MDBox sx={{ width: "100%", minWidth: "800px" }}>
        <MDBox
          display="grid"
          gridTemplateColumns={`${LABEL_WIDTH} repeat(${HOURS.length}, ${isDashboard ? "1fr" : CELL_SIZE})`}
          gap={GAP_SIZE}
          alignItems="center"
        >
          {DAYS.map((day) => (
            <React.Fragment key={day}>
              {/* Day Label */}
              <MDBox display="flex" alignItems="center" justifyContent="flex-start">
                <MDTypography variant="button" fontWeight="bold" color="text.secondary" fontSize="0.75rem">
                  {day}
                </MDTypography>
              </MDBox>
              {HOURS.map((hour, index) => {
      
                const value = data[day]?.[index] || 0;
                
                const opacity = maxValue === 0 ? 0.1 : Math.max(0.1, value / maxValue);

                return (
                  <Tooltip key={`${day}-${hour}`} title={`${day} @ ${formatHour(hour)}: ${value} usage`} arrow>
                    <MDBox
                      sx={{
                        width: "100%",
                        height: CELL_SIZE,
                        borderRadius: "4px",
                        backgroundColor: color,
                        opacity: opacity,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.15)",
                          border: "1px solid #333",
                          zIndex: 2,
                          opacity: 1,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
          <MDBox /> 
          {HOURS.map((hour) => (
            <MDBox key={`label-${hour}`} display="flex" justifyContent="center" mt={1}>
              {/* Show label every 3 hours */}
              {hour % 3 === 0 && (
                <MDTypography variant="caption" color="text.secondary" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                  {formatHour(hour)}
                </MDTypography>
              )}
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

GlobalHeatmap.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  color: PropTypes.string,
  isDashboard: PropTypes.bool,
};

export default GlobalHeatmap;